import React, { createContext, useContext, useState, ReactNode, useCallback } from "react";

// fileSlice.tsから型定義と初期状態を流用
type FileState = {
  fileTitle: { [fileUuid: string]: string };
  fileText: { [fileUuid: string]: string };
  unsplash: { [fileUuid: string]: string };
  generatedImage: { [fileUuid: string]: string };
  thumbnailImage: { [fileUuid: string]: string };
  selection: { [fileUuid: string]: { index: number; length: number; text: string } };
  shouldReplace: { [fileUuid: string]: boolean };
  publicStatus: { [fileUuid: string]: number };
  publicUuid: { [fileUuid: string]: string };
  language: { [fileUuid: string]: string };
  tags: { [fileUuid: string]: string[] };
  saveTrigger: boolean;
  updateThumbnail: boolean;
  sidebarGenerateImage: string;
  voiceGeneratorText: string;
};

const initialState: FileState = {
  fileText: {},
  fileTitle: {},
  unsplash: {},
  generatedImage: {},
  sidebarGenerateImage: "",
  thumbnailImage: {},
  selection: {},
  shouldReplace: {},
  publicStatus: {},
  publicUuid: {},
  language: {},
  tags: {},
  saveTrigger: false,
  updateThumbnail: false,
  voiceGeneratorText: "",
};

// コンテキストの型定義
interface FileContextProps extends FileState {
  setFileText: (payload: { uuid: string; content: string }) => void;
  setFileTitle: (payload: { uuid: string; title: string }) => void;
  setSelection: (payload: { uuid: string; index: number; length: number; text: string }) => void;
  setUnsplash: (payload: { uuid: string; url: string }) => void;
  setGeneratedImage: (payload: { uuid: string; url: string }) => void;
  setSidebarGenerateImage: (payload: string) => void;
  setThumbnailImage: (payload: { uuid: string; url: string }) => void;
  setReplacement: (payload: { uuid: string; index: number; length: number; text: string }) => void;
  setShouldReplace: (payload: { uuid: string; shouldReplace: boolean }) => void;
  setPublicStatus: (payload: { uuid: string; status: number }) => void;
  setPublicUuid: (payload: { uuid: string; publicUuid: string }) => void;
  setLanguage: (payload: { uuid: string; language: string }) => void;
  setTags: (payload: { uuid: string; tags: string[] }) => void;
  setSaveTrigger: (payload: boolean) => void;
  setUpdateThumbnail: (payload: boolean) => void;
  setVoiceGeneratorText: (payload: string) => void;
  clearFileData: (uuid: string) => void;
}

const FileContext = createContext<FileContextProps | undefined>(undefined);

export const useFileContext = () => {
  const context = useContext(FileContext);
  if (!context) {
    throw new Error("useFileContext must be used within a FileProvider");
  }
  return context;
};

interface FileProviderProps {
  children: ReactNode;
}

export const FileProvider = ({ children }: FileProviderProps) => {
  const [state, setState] = useState<FileState>(initialState);

  const setFileText = useCallback((payload: { uuid: string; content: string }) => {
    setState((prevState) => ({
      ...prevState,
      fileText: { ...prevState.fileText, [payload.uuid]: payload.content },
    }));
  }, []);

  const setFileTitle = useCallback((payload: { uuid: string; title: string }) => {
    setState((prevState) => ({
      ...prevState,
      fileTitle: { ...prevState.fileTitle, [payload.uuid]: payload.title },
    }));
  }, []);

  const setSelection = useCallback((payload: { uuid: string; index: number; length: number; text: string }) => {
    setState((prevState) => ({
      ...prevState,
      selection: {
        ...prevState.selection,
        [payload.uuid]: { index: payload.index, length: payload.length, text: payload.text },
      },
    }));
  }, []);

  const setUnsplash = useCallback((payload: { uuid: string; url: string }) => {
    setState((prevState) => ({
      ...prevState,
      unsplash: { ...prevState.unsplash, [payload.uuid]: payload.url },
    }));
  }, []);

  const setGeneratedImage = useCallback((payload: { uuid: string; url: string }) => {
    setState((prevState) => ({
      ...prevState,
      generatedImage: { ...prevState.generatedImage, [payload.uuid]: payload.url },
    }));
  }, []);

  const setSidebarGenerateImage = useCallback((payload: string) => {
    setState((prevState) => ({ ...prevState, sidebarGenerateImage: payload }));
  }, []);

  const setThumbnailImage = useCallback((payload: { uuid: string; url: string }) => {
    setState((prevState) => ({
      ...prevState,
      thumbnailImage: { ...prevState.thumbnailImage, [payload.uuid]: payload.url },
    }));
  }, []);

  const setReplacement = useCallback((payload: { uuid: string; index: number; length: number; text: string }) => {
    setState((prevState) => ({
      ...prevState,
      selection: {
        ...prevState.selection,
        [payload.uuid]: { index: payload.index, length: payload.length, text: payload.text },
      },
    }));
  }, []);

  const setShouldReplace = useCallback((payload: { uuid: string; shouldReplace: boolean }) => {
    setState((prevState) => ({
      ...prevState,
      shouldReplace: { ...prevState.shouldReplace, [payload.uuid]: payload.shouldReplace },
    }));
  }, []);

  const setPublicStatus = useCallback((payload: { uuid: string; status: number }) => {
    setState((prevState) => ({
      ...prevState,
      publicStatus: { ...prevState.publicStatus, [payload.uuid]: payload.status },
    }));
  }, []);

  const setPublicUuid = useCallback((payload: { uuid: string; publicUuid: string }) => {
    setState((prevState) => ({
      ...prevState,
      publicUuid: { ...prevState.publicUuid, [payload.uuid]: payload.publicUuid },
    }));
  }, []);

  const setLanguage = useCallback((payload: { uuid: string; language: string }) => {
    setState((prevState) => ({
      ...prevState,
      language: { ...prevState.language, [payload.uuid]: payload.language },
    }));
  }, []);

  const setTags = useCallback((payload: { uuid: string; tags: string[] }) => {
    setState((prevState) => ({
      ...prevState,
      tags: { ...prevState.tags, [payload.uuid]: payload.tags },
    }));
  }, []);

  const setSaveTrigger = useCallback((payload: boolean) => {
    setState((prevState) => ({ ...prevState, saveTrigger: payload }));
  }, []);

  const setUpdateThumbnail = useCallback((payload: boolean) => {
    setState((prevState) => ({ ...prevState, updateThumbnail: payload }));
  }, []);

  const setVoiceGeneratorText = useCallback((payload: string) => {
    setState((prevState) => ({ ...prevState, voiceGeneratorText: payload }));
  }, []);

  const clearFileData = useCallback((uuid: string) => {
    setState((prevState) => {
      const newState = { ...prevState };

      // 指定されたuuidに関連するすべてのデータを削除
      delete newState.fileText[uuid];
      delete newState.fileTitle[uuid];
      delete newState.unsplash[uuid];
      delete newState.generatedImage[uuid];
      delete newState.thumbnailImage[uuid];
      delete newState.selection[uuid];
      delete newState.shouldReplace[uuid];
      delete newState.publicStatus[uuid];
      delete newState.publicUuid[uuid];
      delete newState.language[uuid];
      delete newState.tags[uuid];

      return newState;
    });
  }, []);

  return (
    <FileContext.Provider
      value={{
        ...state,
        setFileText,
        setFileTitle,
        setSelection,
        setUnsplash,
        setGeneratedImage,
        setSidebarGenerateImage,
        setThumbnailImage,
        setReplacement,
        setShouldReplace,
        setPublicStatus,
        setPublicUuid,
        setLanguage,
        setTags,
        setSaveTrigger,
        setUpdateThumbnail,
        setVoiceGeneratorText,
        clearFileData,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};
